import React from 'react';
import { useTranslation } from 'react-i18next';

import { CheckHeader } from 'Src/feature/check/models';
import { useCheckHeader } from 'Src/feature/check/useCheckHeader';
import { useChecksPage } from 'Src/feature/checks/useChecksPage';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { FloorPlanViewerProviderProps } from 'Src/feature/floor-plan/useFloorPlanViewerStore';
import { useCurrentOperator } from 'Src/feature/operator/useCurrentOperator';
import { findRevenueCenterByLocationRevenueCenterId, useRevenueCenters } from 'Src/feature/revenue-center/useRevenueCenter';
import { ServiceError } from 'Src/network/useError';

import { FloorPlan } from './floorPlanService';
import { useFloorPlan } from './useFloorPlan';

const useFloorPlanPage = () => {
  const { t } = useTranslation();
  const checksPage = useChecksPage();
  const { employeeId, jobPositionId } = useCurrentOperator();
  const { data: allRevenueCenters } = useRevenueCenters.query.getAll();
  const floorPlanList = useFloorPlan.query.list();
  const tableList = useFloorPlan.query.tableList();
  const employeeGetAllMap = useEmployee.query.map();
  const checkHeader = useCheckHeader.query.collection();

  const checkPageTabSelection = checksPage.currentSelection;
  const selectedRevenueCenter = allRevenueCenters?.find(({ id }) => id === checkPageTabSelection);
  // const [selectedFloorPlan, selectedFloorPlanSet] = useState(undefined as undefined | FloorPlan);
  const revenueCenterFloorPlans = (floorPlanList.data || []).filter((p) => {
    if (!selectedRevenueCenter) return false;
    return findRevenueCenterByLocationRevenueCenterId(allRevenueCenters || [], p.locationRevenueCenterId)?.id === selectedRevenueCenter.id;
  });
  const defaultFp = revenueCenterFloorPlans?.find((p) => p.isDefault);
  let selectedFloorPlan: FloorPlan | undefined = undefined;
  if (defaultFp) selectedFloorPlan = defaultFp;

  const selectedFloorPlanTableIds = selectedFloorPlan?.layout.map((l) => l.tableId);
  const tableSet = tableList.data?.filter((t) => selectedFloorPlanTableIds?.includes(t.id));

  const providerProps: FloorPlanViewerProviderProps = {
    employeeId,
    jobPositionId,
    getCheckIdFromTable: (tableId: number) => {
      const layouts = (selectedFloorPlan?.layout || []);
      const shape = layouts.find((obj) => obj.tableId === tableId);
      let chkHeader: CheckHeader | undefined = undefined;
      if (shape?.name) {
        chkHeader = (checkHeader.data?.all || []).find((hdr) => hdr.tableId === shape?.tableId && !['Closed', 'Voided', 'MergeVoided'].includes(hdr.status));
      }
      return chkHeader?.id;
    },
    getEmployeeById: (employeeId: number) => employeeGetAllMap.data?.get(employeeId),
  };

  const errors:ServiceError[] = [];
  const isLoading = floorPlanList.isLoading || checkHeader.isLoading || tableList.isLoading;
  if (!floorPlanList.isLoading && floorPlanList.isError && floorPlanList.error) {
    errors.push(floorPlanList.error);
  }
  if (!checkHeader.isLoading && checkHeader.isError && checkHeader.error) {
    errors.push(checkHeader.error);
  }
  if (!tableList.isLoading && tableList.isError && tableList.error) {
    errors.push(tableList.error);
  }
  if (!allRevenueCenters || !allRevenueCenters.length) {
    errors.push(new ServiceError({ title: t('floorPlan.viewer.errors.noRevCentersHeading'), message: t('floorPlan.viewer.errors.noRevCentersMsg') }));
  }
  
  return {
    isLoading,
    errors,
    selected: {
      revenueCenter: selectedRevenueCenter,
      floorPlan: selectedFloorPlan,
      tableSet,
    },
    providerProps,
  };
};

export { useFloorPlanPage };
